import { AnimatePresence, motion } from "motion/react"

import type { GroupedActivityMessage } from "../../types/activity-message-types"

import { Check } from "@/components/icons"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

type ActivityNavigatorProps = {
  messages: GroupedActivityMessage[]
  activeSection: number
  onSectionClick: (index: number) => void
  isLoading?: boolean
}

export function ActivityNavigator({
  messages,
  activeSection,
  onSectionClick,
  isLoading = false,
}: ActivityNavigatorProps) {
  return (
    <section className="bg-muted/10 relative flex h-full w-full flex-col rounded-[4px]">
      {/* Fade gradient for mobile horizontal scroll */}
      <div className="from-background pointer-events-none absolute top-0 left-0 z-10 block h-full w-12 bg-gradient-to-r to-transparent md:hidden" />

      {/* Combined timeline and titles for proper alignment */}
      <section className="hide-scrollbar flex flex-row gap-6 overflow-x-auto overflow-y-hidden py-4 md:flex-col md:gap-0 md:overflow-x-hidden md:overflow-y-auto">
        <AnimatePresence>
          {messages.map((group, index) => (
            <motion.div
              key={`nav-item-${index}`}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              className="relative flex shrink-0 flex-col items-center px-4 md:flex-row md:items-start md:gap-3 md:pb-6"
              exit={{ opacity: 0, scale: 0.9, x: -20 }}
              initial={{ opacity: 0, scale: 0.9, x: -10 }}
              transition={{
                duration: 0.5,
                delay: index * 0.15, // stagger timeline expansion
                ease: [0.23, 1, 0.32, 1],
              }}
            >
              {/* Mobile horizontal connecting line - positioned relative to parent */}
              {index < messages.length - 1 && (
                <motion.div
                  animate={{ scaleX: 1, opacity: 1 }}
                  className="bg-success/10 absolute top-[8px] left-1/2 h-[2px] w-[calc(100%+1.5rem)] origin-left md:hidden"
                  initial={{ scaleX: 0, opacity: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.15 + 0.3, // appear after timeline item
                    ease: [0.23, 1, 0.32, 1],
                  }}
                />
              )}

              {/* Desktop vertical connecting line - positioned relative to parent */}
              {index < messages.length - 1 && (
                <motion.div
                  animate={{ scaleY: 1, opacity: 1 }}
                  className="bg-success/10 absolute top-[8px] left-[25px] hidden h-[calc(100%-0.5rem)] w-[2px] origin-top md:block"
                  initial={{ scaleY: 0, opacity: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.15 + 0.3, // appear after timeline item
                    ease: [0.23, 1, 0.32, 1],
                  }}
                />
              )}

              {/* Timeline status indicator container */}
              <div className="relative mb-2 flex size-5 items-center justify-center md:mb-0">
                <motion.div
                  animate={{ scale: 1, opacity: 1 }}
                  initial={{ scale: 0.5, opacity: 0 }}
                  className={cn(
                    "border-foreground bg-success flex size-5 items-center justify-center rounded-full border",
                    {
                      "bg-muted": isLoading && index === messages.length - 1,
                    }
                  )}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.15 + 0.1, // appear shortly after timeline item starts
                    ease: [0.23, 1, 0.32, 1],
                  }}
                >
                  {isLoading && index === messages.length - 1 ? (
                    <Spinner className="text-muted-foreground size-3" />
                  ) : (
                    <Check className="text-success-foreground size-3" />
                  )}
                </motion.div>
              </div>

              {/* Title button */}
              <button
                className={cn(
                  "hover:text-foreground flex max-w-24 items-center gap-2 text-center text-sm transition-colors md:-mt-[2px] md:max-w-none md:text-left md:text-base md:whitespace-normal",
                  {
                    "text-foreground font-semibold": activeSection === index,
                    "text-muted-foreground font-medium": activeSection !== index,
                  }
                )}
                onClick={() => {
                  onSectionClick(index)
                }}
              >
                {group.title}
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </section>
    </section>
  )
}
